import {
  createContext,
  useState,
  useEffect,
  useMemo,
  useContext,
  useRef,
} from 'react';
import { useAuth } from 'react-oidc-context';
import axios from 'axios';

import { useTenant } from './TenantContext';

const DEFAULT_BRANDING = Object.freeze({
  app_name: 'Stackbd',
  logo_url: null,
  favicon_url: null,
  primary_color: '#c756ec',
  secondary_color: '#8834fd',
  support_email: 'contact@stackbd.com',
  terms_url: null,
  privacy_url: null,
});

const DEFAULT_HOSTS = new Set([
  'localhost',
  '127.0.0.1',
  'app.stackbd.com',
  'dev-app.stackbd.com',
]);

const BrandingContext = createContext({
  branding: DEFAULT_BRANDING,
  isWhitelabel: false,
  isLoading: false,
  refresh: () => {},
});

function isCustomDomain() {
  if (typeof window === 'undefined') return false;
  // REQ-17: optional test/dev override
  if (window.__WL_FORCE_DOMAIN__) return true;
  const host = window.location.hostname;
  return !DEFAULT_HOSTS.has(host);
}

function applyBranding(branding) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  if (branding.primary_color) root.style.setProperty('--primary-color', branding.primary_color);
  if (branding.secondary_color) root.style.setProperty('--secondary-color', branding.secondary_color);
  // REQ-14: title + favicon
  if (branding.app_name) document.title = branding.app_name;
  if (branding.favicon_url) {
    let link = document.querySelector('link[rel="icon"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = branding.favicon_url;
  }
}

const getApiBase = () => process.env.REACT_APP_API_URL || 'http://localhost:7123';

function BrandingProvider({ children }) {
  const auth = useAuth();
  const { tenant } = useTenant();
  const [branding, setBranding] = useState(DEFAULT_BRANDING);
  const [isLoading, setIsLoading] = useState(false);
  const [isWhitelabel, setIsWhitelabel] = useState(false);
  const cacheKey = useRef(null);

  const refresh = async () => {
    if (typeof window === 'undefined') return;
    setIsLoading(true);
    try {
      let data = null;
      // REQ-17: pre-auth path uses by-domain when on a custom hostname
      const forcedDomain = window.__WL_FORCE_DOMAIN__;
      const host = forcedDomain || window.location.hostname;
      if (!auth.user && isCustomDomain()) {
        const res = await axios.get(`${getApiBase()}/v1/branding/by-domain`, {
          params: { domain: host },
        });
        data = res.data;
      } else if (auth.user && tenant) {
        const res = await axios.get(`${getApiBase()}/v1/branding`, {
          headers: {
            Authorization: `Bearer ${auth.user.id_token}`,
            tenant,
          },
        });
        data = res.data;
      }
      if (data) {
        const next = { ...DEFAULT_BRANDING, ...data };
        setBranding(next);
        setIsWhitelabel(next.app_name && next.app_name !== DEFAULT_BRANDING.app_name);
        applyBranding(next);
      } else {
        setBranding(DEFAULT_BRANDING);
        applyBranding(DEFAULT_BRANDING);
      }
    } catch (e) {
      // Branding API down → fall back to defaults silently
      setBranding(DEFAULT_BRANDING);
      applyBranding(DEFAULT_BRANDING);
    } finally {
      setIsLoading(false);
    }
  };

  // NFR-02: only refetch when the tenant or auth identity changes
  useEffect(() => {
    const key = `${auth.user ? 'auth' : 'pub'}:${tenant || ''}:${typeof window !== 'undefined' ? window.location.hostname : ''}`;
    if (cacheKey.current === key) return;
    cacheKey.current = key;
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.user, tenant]);

  const value = useMemo(
    () => ({ branding, isWhitelabel, isLoading, refresh }),
    [branding, isWhitelabel, isLoading]
  );

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
}

export const useBranding = () => useContext(BrandingContext);
export { BrandingProvider, BrandingContext, DEFAULT_BRANDING };
